import React from 'react';
import EditableContent from '../EditableContent.tsx';

interface HeroSectionProps {
  isEditMode: boolean;
  getContent: (key: string) => string;
  onContentSave: (contentId: string, value: string, contentType: 'text' | 'image') => Promise<void> | void;
  onDirtyStateChange: (contentId: string, isDirty: boolean) => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  isEditMode,
  getContent,
  onContentSave,
  onDirtyStateChange,
}) => {
  const editable = (
    contentId: string,
    tag: 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span' | 'li' | 'dt' | 'dd' | 'small' | 'strong' | 'em',
    className?: string,
    multiline?: boolean
  ) => (
    <EditableContent
      contentId={contentId}
      contentType="text"
      tag={tag}
      className={className}
      multiline={multiline}
      defaultValue={getContent(contentId)}
      isEditing={isEditMode}
      onSave={onContentSave}
      onDirtyStateChange={onDirtyStateChange}
    />
  );

  return (
    <>
      <section id="home" className="hero">
        <div className="hero-shade"></div>
        <div className="hero-top">
          {editable('hero-eyebrow', 'span', 'eyebrow')}
          {editable('hero-edition', 'span', 'edition')}
        </div>
        <div className="hero-copy">
          <p className="kicker">
            <span></span> {editable('hero-kicker', 'span')}
          </p>
          <h1>
            {editable('hero-heading-line1', 'span')}
            <br />
            {editable('hero-heading-line2', 'span')}
            <span className="outline">{editable('hero-heading-accent', 'span')}</span>
          </h1>
          {editable('hero-intro-text', 'p', 'intro', true)}
          {editable('hero-principle', 'p', 'hero-principle', true)}
          {isEditMode ? (
            editable('hero-cta-text', 'span')
          ) : (
            <a className="button light" href="#products">
              {getContent('hero-cta-text')} <span>↗</span>
            </a>
          )}
        </div>
        <div className="image-tag">
          {editable('hero-tag-label', 'span')}
          {editable('hero-tag-strong', 'strong')}
        </div>
        <div className="hero-bottom">
          <a href="#products" className="scroll-cue">
            <span>↓</span> {editable('hero-scroll-text', 'span')}{' '}
            {editable('hero-scroll-sub', 'small')}
          </a>
          {editable('hero-quote', 'p')}
          <span className="page-index">01 — 04</span>
        </div>
      </section>
      {isEditMode && (
        <div className="hero-image-edit-panel">
          <h3>首頁視覺圖片</h3>
          <p>
            建議使用 1536×1024、主體置中且帶透明邊距的 PNG（螺絲動畫的縮放以此尺寸計算），
            上傳後儲存即會套用至首頁動畫。
          </p>
          <div className="hero-image-edit-item">
            <span>螺絲圖片</span>
            <EditableContent
              contentId="hero-screw-image"
              contentType="image"
              defaultValue={getContent('hero-screw-image')}
              isEditing={true}
              onSave={onContentSave}
              onDirtyStateChange={onDirtyStateChange}
            />
          </div>
          <div className="hero-image-edit-item">
            <span>火花圖片</span>
            <EditableContent
              contentId="hero-sparks-image"
              contentType="image"
              defaultValue={getContent('hero-sparks-image')}
              isEditing={true}
              onSave={onContentSave}
              onDirtyStateChange={onDirtyStateChange}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default HeroSection;
